/**
 * Pure helpers shared by the builder modal and the Strategies tab. No React, no API —
 * so the rule shape stays in one place and both views describe a strategy identically.
 */

export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

export const OPS = [
  { value: 'cross_above', label: 'crosses above', labelKey: 'ea.ops.crossesAbove' },
  { value: 'cross_below', label: 'crosses below', labelKey: 'ea.ops.crossesBelow' },
  { value: '>', label: 'is above', labelKey: 'ea.ops.isAbove' },
  { value: '>=', label: 'is above or equal', labelKey: 'ea.ops.isAboveOrEqual' },
  { value: '<', label: 'is below', labelKey: 'ea.ops.isBelow' },
  { value: '<=', label: 'is below or equal', labelKey: 'ea.ops.isBelowOrEqual' },
];

export const PRICE_FIELDS = ['close'];   // shown as "Price" in the UI; single option matching EaPanel

export const TF_LABEL = { '1m': 'M1', '5m': 'M5', '15m': 'M15', '30m': 'M30', '1h': 'H1', '4h': 'H4', '1d': 'D1' };
export const TF_MINUTES = { '1m': 1, '5m': 5, '15m': 15, '30m': 30, '1h': 60, '4h': 240, '1d': 1440 };

export const tfDisplay = (tf) => TF_LABEL[tf] || tf;
export const opNeedsPrevBar = (op) => op === 'cross_above' || op === 'cross_below';
export const opLabel = (op, t) => {
  const found = OPS.find((o) => o.value === op);
  if (!found) return op;
  return t ? t(found.labelKey) : found.label;
};
export const localizedOps = (t) =>
  OPS.map((o) => ({ value: o.value, label: t ? t(o.labelKey) : o.label }));

/**
 * Bars of history an indicator needs before its value matches the chart.
 * Mirrors server warmup table in docs/ea-system/INDICATOR_WARMUP.md (approximate).
 */
export function warmupFor(code, params = {}) {
  const p = Math.max(2, Number(params?.period) || 14);
  switch (code) {
    case 'rsi':
    case 'atr':
      return p * 11 + 1;
    case 'trix':
      return p * 13 + 1;
    case 'awesome_oscillator':
      return Math.max(2, Number(params?.slow) || 34) + 1;
    case 'typical_price':
      return Math.max(2, Number(params?.period) || 20);
    case 'momentum':
      return Math.max(2, Number(params?.period) || 14) + 1;
    case 'ma':
    case 'ma_deviation': {
      const type = String(params?.type || 'simple').toLowerCase();
      const period = Math.max(2, Number(params?.period) || 20);
      return type === 'exponential' ? period * 6 : period;
    }
    case 'williams_r':
    case 'cci':
    case 'std_dev':
      return Math.max(2, Number(params?.period) || 20);
    default:
      return Math.max(2, Number(params?.period) || 20);
  }
}

const CODE_LABELS = {
  typical_price: 'Typical Price',
  momentum: 'Momentum',
  std_dev: 'Std Dev',
  cci: 'CCI',
  williams_r: 'Williams %R',
  atr: 'ATR',
  awesome_oscillator: 'AO',
  trix: 'Trix',
  ma_deviation: 'MA Dev',
  rsi: 'RSI',
};

/** Short human label for an indicator: `EMA 9`, `RSI 14`, `AO 5/34`. */
export function indicatorLabel(ind, t) {
  if (!ind) return '';
  if (ind.code === 'ma') {
    const period = ind.params?.period ?? '';
    const t = String(ind.params?.type || 'simple').toLowerCase();
    const prefix = t === 'exponential' ? 'EMA' : t === 'weighted' ? 'WMA' : 'SMA';
    return `${prefix} ${period}`.trim();
  }
  if (ind.code === 'awesome_oscillator') {
    return `AO ${ind.params?.fast ?? 5}/${ind.params?.slow ?? 34}`;
  }
  if (ind.code === 'typical_price') {
    const period = ind.params?.period ?? 20;
    return `Typical Price ${period}`;
  }
  const prefix = CODE_LABELS[ind.code] || String(ind.code).toUpperCase();
  const period = ind.params?.period;
  return period != null && period !== '' ? `${prefix} ${period}` : prefix;
}

/** Label for either side of a condition — an indicator alias or a raw price field. */
// 'close' is shown as "Price" to match EaPanel UX; other fields show capitalized.
const PRICE_LABEL = { close: 'Price', open: 'Open', high: 'High', low: 'Low' };
const PRICE_LABEL_KEYS = {
  close: 'ea.priceLabel',
  open: 'ea.openLabel',
  high: 'ea.highLabel',
  low: 'ea.lowLabel',
};
export function operandLabel(operand, picked, t) {
  if (PRICE_LABEL_KEYS[operand]) {
    return t ? t(PRICE_LABEL_KEYS[operand]) : PRICE_LABEL[operand];
  }
  const ind = (picked || []).find((p) => p.alias === operand);
  return ind ? indicatorLabel(ind, t) : String(operand);
}

/**
 * Aliases are what the rule engine matches on, so they must be unique and stable.
 * Derived from the indicator itself (`ema_9`) rather than a fixed fast/slow slot.
 */
export function aliasForIndicator(code, params, existing) {
  let base;
  if (code === 'typical_price') {
    base = `tp_${params?.period ?? 20}`;
  } else if (code === 'awesome_oscillator') {
    base = `ao_${params?.fast ?? 5}_${params?.slow ?? 34}`;
  } else if (code === 'ma') {
    const t = String(params?.type || 'simple').toLowerCase();
    const maCode = t === 'exponential' ? 'ema' : t === 'weighted' ? 'wma' : 'sma';
    base = `${maCode}_${params?.period ?? ''}`;
  } else {
    const period = params?.period ?? '';
    base = period !== '' ? `${code}_${period}` : code;
  }
  const taken = new Set((existing || []).map((p) => p.alias));
  let alias = base;
  let n = 2;
  while (taken.has(alias)) alias = `${base}_${n++}`;
  return alias;
}

/** Validate indicator params against catalog param_schema (client-side, mirrors server). */
export function validateIndicatorParams(ind, def, t) {
  const message = (key, param, fallback, extra = {}) => t ? t('ea.' + key, { indicator: indicatorLabel(ind, t), param: t('ea.params.' + param, { defaultValue: param }), ...extra }) : fallback;
  const schema = def?.param_schema;
  if (!schema || typeof schema !== 'object' || !Object.keys(schema).length) return null;
  const params = ind.params || {};
  for (const [key, rule] of Object.entries(schema)) {
    const raw = params[key];
    const val = raw === undefined || raw === null || raw === '' ? rule?.default : raw;
    if (val === undefined || val === null || val === '') {
      return message('paramMissing', key, `${indicatorLabel(ind)} is missing ${key}`);
    }
    if (rule?.type === 'number') {
      const n = Number(val);
      if (!Number.isFinite(n)) return message('paramNumber', key, `${indicatorLabel(ind)}: ${key} must be a number`);
      if (rule.min != null && n < rule.min) return message('paramMin', key, `${indicatorLabel(ind)}: ${key} must be at least ${rule.min}`, { limit: rule.min });
      if (rule.max != null && n > rule.max) return message('paramMax', key, `${indicatorLabel(ind)}: ${key} must be at most ${rule.max}`, { limit: rule.max });
      if (!Number.isInteger(n)) return message('paramInteger', key, `${indicatorLabel(ind)}: ${key} must be a whole number`);
    }
    if (rule?.type === 'enum' && Array.isArray(rule.values) && !rule.values.includes(String(val))) {
      return message('paramEnum', key, `${indicatorLabel(ind)}: ${key} must be one of ${rule.values.join(', ')}`, { values: rule.values.map(v => t ? t('ea.enum.' + v, { defaultValue: v }) : v).join(', ') });
    }
  }
  return null;
}

export const newCondition = (aliases) => ({
  left: aliases?.[0] || 'close',
  op: aliases?.length >= 2 ? 'cross_above' : '>',
  rightKind: aliases?.length >= 2 ? 'ref' : 'value',
  rightValue: '30',
  rightRef: aliases?.[1] || 'close',
});

export const toServerCondition = (c) => ({
  left: c.left,
  op: c.op,
  right: c.rightKind === 'value' ? Number(c.rightValue) : c.rightRef,
});

/** Server rule -> editor rows. `right` is a number for a literal, a string for a reference. */
export function fromServerRule(rule, fallbackAliases) {
  const conds = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (!conds.length) return [newCondition(fallbackAliases)];
  return conds.map((c) => {
    const isValue = typeof c.right === 'number' || (c.right !== '' && !Number.isNaN(Number(c.right)));
    return {
      left: c.left,
      op: c.op,
      rightKind: isValue ? 'value' : 'ref',
      rightValue: isValue ? String(c.right) : '30',
      rightRef: isValue ? fallbackAliases?.[1] || 'close' : String(c.right),
    };
  });
}

export function conditionText(c, picked, t) {
  const right = c.rightKind === 'value' ? c.rightValue : operandLabel(c.rightRef, picked, t);
  return `${operandLabel(c.left, picked, t)} ${opLabel(c.op, t)} ${right}`;
}

/**
 * The plain-language summary under the builder — the check that the rules say what the
 * user meant before real money is committed.
 */
export function plainWords({
  tf, picked, entryConds, entryLogic, entryAction, exitConds, exitLogic,
  exitEnabled, lotSize, slOn, stopLossPnl, tpOn, takeProfitPnl,
}, t) {
  if (!(entryConds || []).length) {
    return t ? t('ea.plain.addEntry') : 'Add an entry condition.';
  }
  const andWord = t ? ` ${t('ea.and')} ` : ' and ';
  const orWord = t ? ` ${t('ea.or')} ` : ' or ';
  const join = (logic) => (logic === 'AND' ? andWord : orWord);
  const entry = (entryConds || []).map((c) => conditionText(c, picked, t)).join(join(entryLogic));
  const risk = [];
  if (slOn && Number(stopLossPnl) > 0) risk.push(`SL PNL ${stopLossPnl}`);
  if (tpOn && Number(takeProfitPnl) > 0) risk.push(`TP PNL ${takeProfitPnl}`);
  const riskText = risk.length ? ` (${risk.join(' / ')})` : '';
  const exitParts = [];
  if (exitEnabled && exitConds?.length) {
    const whenConds = exitConds.map((c) => conditionText(c, picked, t)).join(join(exitLogic));
    exitParts.push(t ? t('ea.plain.when', { conds: whenConds }) : `when ${whenConds}`);
  }
  if (slOn && Number(stopLossPnl) > 0) exitParts.push(`SL PNL ${stopLossPnl}`);
  if (tpOn && Number(takeProfitPnl) > 0) exitParts.push(`TP PNL ${takeProfitPnl}`);
  const actionKey = String(entryAction).toUpperCase() === 'SELL' ? 'sell' : 'buy';
  const actionWord = t
    ? t(actionKey === 'sell' ? 'ea.plain.sellAction' : 'ea.plain.buyAction')
    : actionKey;
  const exitText = exitParts.length
    ? (t
      ? ` ${t('ea.plain.close', { parts: exitParts.join(orWord) })}`
      : ` Close ${exitParts.join(' or ')}.`)
    : (t ? ` ${t('ea.plain.noAutoClose')}` : ' No automatic close — disable manually.');
  if (t) {
    return t('ea.plain.summary', {
      tf: tfDisplay(tf),
      entry,
      action: actionWord,
      lotSize,
      risk: riskText,
      exit: exitText,
    });
  }
  return `On each ${tfDisplay(tf)} close, if ${entry}, open a ${actionWord} of ${lotSize} lot${riskText}.${exitText}`;
}

/** Warmup driver + total bars the strategy needs before it can fire. */
export function warmupSummary(picked, entryConds, exitConds) {
  if (!picked?.length) return { bars: 0, driver: null };
  let bars = 0;
  let driver = null;
  picked.forEach((p) => {
    const w = warmupFor(p.code, p.params);
    if (w > bars) { bars = w; driver = p.alias; }
  });
  const lookback = [...(entryConds || []), ...(exitConds || [])].some((c) => opNeedsPrevBar(c.op)) ? 1 : 0;
  return { bars: bars + lookback + 1, driver, lookback };
}
